import React from "react";
import { ChevronRight } from "lucide-react";
import type { NavigationItem } from "../types";
import { NavItemLink } from "../nav-item-link";

interface DesktopDropdownChildProps {
  child: NavigationItem;
}

/**
 * A single submenu entry inside a desktop nav dropdown, including its optional
 * nested (grandchild) flyout. Uses focus-within + tabIndex for iPad support per
 * IPAD_TOUCH_INTERACTIONS — never touch handlers or client state.
 */
export function DesktopDropdownChild({ child }: DesktopDropdownChildProps) {
  const ChildIcon = child.icon;
  const childHasChildren = !!child.children && child.children.length > 0;

  return (
    <div
      className="relative group/submenu focus-within:z-50"
      tabIndex={childHasChildren ? 0 : undefined}
    >
      <NavItemLink
        item={child}
        className="flex items-center px-4 py-3 text-sm text-popover-foreground hover:bg-accent hover:text-popover-foreground transition-colors min-h-[44px] md:min-h-[48px]"
      >
        {ChildIcon && (
          <ChildIcon className="mr-3 h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
        )}
        <div className="flex-1">
          <div className="font-medium">{child.name}</div>
          {child.description && (
            <div className="text-xs text-muted-foreground mt-0.5">{child.description}</div>
          )}
        </div>
        {childHasChildren && (
          <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
      </NavItemLink>

      {/* Nested submenu for children with their own children */}
      {childHasChildren && (
        <>
          {/* Invisible hover bridge for nested submenu */}
          <div className="absolute top-0 left-full w-4 h-full opacity-0 group-hover/submenu:opacity-100 pointer-events-none group-hover/submenu:pointer-events-auto"></div>

          <div className="absolute top-0 left-full ml-2 w-56 bg-popover border border-border rounded-lg shadow-xl invisible opacity-0 scale-95 group-hover/submenu:visible group-hover/submenu:opacity-100 group-hover/submenu:scale-100 group-focus-within/submenu:visible group-focus-within/submenu:opacity-100 group-focus-within/submenu:scale-100 transition-all duration-200 z-50 pointer-events-none group-hover/submenu:pointer-events-auto group-focus-within/submenu:pointer-events-auto">
            <div className="py-2">
              {child.children?.map((grandchild) => {
                const GrandchildIcon = grandchild.icon;
                return (
                  <NavItemLink
                    key={grandchild.name}
                    item={grandchild}
                    className="flex items-center px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-popover-foreground transition-colors min-h-[44px] md:min-h-[48px]"
                  >
                    {GrandchildIcon && (
                      <GrandchildIcon className="mr-3 h-4 w-4 md:h-5 md:w-5 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium">{grandchild.name}</div>
                      {grandchild.description && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {grandchild.description}
                        </div>
                      )}
                    </div>
                  </NavItemLink>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
